import EventNoteIcon from '@mui/icons-material/EventNote';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';

// Keyed by the real event_type strings from notifications/registry.py
export const NOTIFICATION_ICON_MAP = {
  'leave.submitted': { icon: EventNoteIcon, bg: '#E8F5E9', fg: '#2E7D32' },
  'leave.approved': { icon: EventNoteIcon, bg: '#E8F5E9', fg: '#2E7D32' },
  'leave.rejected': { icon: EventNoteIcon, bg: '#E8F5E9', fg: '#2E7D32' },
  'leave.cancelled': { icon: EventNoteIcon, bg: '#E8F5E9', fg: '#2E7D32' },
  'payroll.payslip_ready': { icon: DescriptionIcon, bg: '#E3F2FD', fg: '#1565C0' },
  'payroll.logout_reminder': { icon: DescriptionIcon, bg: '#E3F2FD', fg: '#1565C0' },
  'attendance.checkout_reminder': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.punch_correction_submitted': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.punch_correction_approved': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.punch_correction_rejected': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.missing_punch_reminder': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.missing_punch_escalation': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.break_started': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.break_ended': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'attendance.break_overdue': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'tasks.assigned': { icon: AssignmentIcon, bg: '#F3E5F5', fg: '#6A1B9A' },
  'tasks.overdue': { icon: AssignmentIcon, bg: '#F3E5F5', fg: '#6A1B9A' },
  'tasks.timer_reminder': { icon: AssignmentIcon, bg: '#F3E5F5', fg: '#6A1B9A' },
  'tasks.timer_limit': { icon: AssignmentIcon, bg: '#F3E5F5', fg: '#6A1B9A' },
  'tasks.commented': { icon: GroupsIcon, bg: '#F3E5F5', fg: '#6A1B9A' },
  'shifts.assigned': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'shifts.swap_requested': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'shifts.reminder': { icon: AccessTimeIcon, bg: '#FFF3E0', fg: '#E65100' },
  'crm.missed_call': { icon: PhoneMissedIcon, bg: '#FFEBEE', fg: '#C62828' },
  'crm.lead_assigned': { icon: PhoneMissedIcon, bg: '#FFEBEE', fg: '#C62828' },
  'system.employee_added': { icon: InfoIcon, bg: '#F5F5F5', fg: '#616161' },
  'system.biometric_offline': { icon: InfoIcon, bg: '#F5F5F5', fg: '#616161' },
  'system.scheduled': { icon: InfoIcon, bg: '#F5F5F5', fg: '#616161' },
};
const DEFAULT_ICON = { icon: InfoIcon, bg: '#F5F5F5', fg: '#616161' };

export const getNotificationIcon = (eventType) =>
  NOTIFICATION_ICON_MAP[eventType] || DEFAULT_ICON;

// The notifications API returns created_at as "DD-MM-YYYY hh:mm AM/PM" (e.g.
// "31-08-2026 05:06 PM") — not an ISO string, so the native Date constructor
// can't parse it (it reads "31" as a month and silently returns Invalid
// Date). Every formatter below goes through this instead.
const parseNotificationTimestamp = (value) => {
  if (!value) return null;
  const match = /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(
    String(value).trim(),
  );
  if (match) {
    const [, day, month, year, hourStr, minute, meridiem] = match;
    let hour = Number(hourStr) % 12;
    if (meridiem.toUpperCase() === 'PM') hour += 12;
    return new Date(Number(year), Number(month) - 1, Number(day), hour, Number(minute));
  }
  // Fall back to native parsing in case another endpoint ever sends ISO.
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

export const formatTime = (createdAtValue) => {
  const date = parseNotificationTimestamp(createdAtValue);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Mentions have no dedicated event_type — they're tasks.commented rows
// stamped with this exact title by send_mention_notification() in signals.py.
export const isMention = (notif) =>
  notif.event_type === 'tasks.commented' && notif.title === 'You were mentioned';

// "leave.approved" -> "Leave Approved", "tasks.commented" -> "Tasks Commented"
export const humanizeEventType = (eventType) => {
  if (!eventType) return 'Notification';
  return eventType
    .split('.')
    .join(' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Buckets a notification's timestamp into a Today/Yesterday/Earlier group for
// the bell panel's grouped list — calendar-day comparison (midnight to
// midnight), not a rolling 24h window, so "Yesterday" means yesterday's date
// rather than "sometime in the last 1-2 days".
export const getNotificationDateGroup = (createdAtValue) => {
  const date = parseNotificationTimestamp(createdAtValue);
  if (!date) return 'Earlier';

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return 'Earlier';
};

export const NOTIFICATION_DATE_GROUPS = ['Today', 'Yesterday', 'Earlier'];

// Groups an already-ordered (newest-first) notification list into
// Today/Yesterday/Earlier buckets, preserving each item's relative order.
export const groupNotificationsByDate = (notifications) => {
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  notifications.forEach((notif) => {
    groups[getNotificationDateGroup(notif.created_at)].push(notif);
  });
  return groups;
};

// Per-row timestamp for the grouped list: bare time under "Today" (the
// section header already says today), "Yesterday, <time>" under "Yesterday",
// and a bare date under "Earlier" — the date grouping already told the reader
// which day a Today/Yesterday row happened on, so repeating it there would be
// redundant, while an Earlier row has no such heading to lean on.
export const formatNotificationTimestamp = (createdAtValue) => {
  const date = parseNotificationTimestamp(createdAtValue);
  if (!date) return '';

  // hour12 forced explicitly — some locales (e.g. en-IN) default this to
  // 24h, but the source data is itself stamped AM/PM, so the display should
  // always read as 12h regardless of the viewer's locale.
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const group = getNotificationDateGroup(createdAtValue);

  if (group === 'Today') return time;
  if (group === 'Yesterday') return `Yesterday, ${time}`;
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatFullTimestamp = (value) => {
  const date = parseNotificationTimestamp(value);
  if (!date) return '-';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true,
  });
};
